import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  BookOpen, Calculator, BarChart3, FileText, Users, Shield, Globe, Clock, CheckCircle2, 
  ArrowRight, Menu, X, Calendar, CreditCard, Zap, TrendingUp, PieChart, Target, 
  Receipt, FileSignature, Building2, DollarSign, Percent, FileSpreadsheet, 
  ChevronRight, ChevronDown, Play, Star, Sparkles, Loader2, Search, Phone, Mail, MapPin, Check, LogOut
} from "lucide-react";

import { API_URL } from "@/utils/api";

// Lime/Neon Green Color
const LIME = "#BFFF00";
const LIME_BG = "#F0FFC2";

// Original Services
const SERVICES = [
  { icon: BookOpen, title: "Bookkeeping", description: "Monthly bookkeeping with accurate categorization and reconciliation. Tax-ready books, always." },
  { icon: Receipt, title: "Invoicing & AR", description: "Create professional invoices, automate reminders, and get paid faster with integrated payments." },
  { icon: Calculator, title: "Tax Planning", description: "Proactive tax strategy to maximize deductions and minimize your liability year-round." },
  { icon: BarChart3, title: "Financial Reporting", description: "Real-time dashboards, P&L statements, balance sheets, and cash flow reports." },
  { icon: TrendingUp, title: "FP&A Services", description: "Financial planning, budgeting, forecasting, and analysis to fuel growth." },
  { icon: Shield, title: "Compliance", description: "Stay compliant with federal, state, and local regulations. 1099s, payroll taxes, and more." }
];

// Original Free Tools
const FREE_TOOLS = [
  { name: "Tax Calculator", description: "Estimate your federal and state tax liability instantly", path: "/tools/tax-calculator", icon: Calculator },
  { name: "Invoice Generator", description: "Create and download professional invoices for free", path: "/tools/invoice", icon: FileText },
  { name: "Expense Tracker", description: "Track and categorize your business expenses", path: "/tools/expenses", icon: Receipt }
];

// Original Pricing (FinAI)
const PRICING_PLANS = [
  { id: "starter", name: "Starter", price: "Free", period: "", description: "For creators just getting started", features: ["Up to $25K annual transactions", "Basic bookkeeping", "Invoice generator", "Expense tracking"], cta: "Start Free" },
  { id: "growth", name: "Growth", price: "$149", period: "/month", description: "For growing businesses", features: ["Up to $100K annual transactions", "Full bookkeeping + reconciliation", "KPI dashboards", "Dedicated bookkeeper"], popular: true, cta: "Get Started" },
  { id: "scale", name: "Scale", price: "$349", period: "/month", description: "For established businesses", features: ["Unlimited transactions", "Advanced reporting", "Tax strategy planning", "Priority support"], cta: "Get Started" },
  { id: "enterprise", name: "Enterprise", price: "Custom", period: "", description: "For complex operations", features: ["Everything in Scale", "Dedicated account manager", "Custom integrations", "CFO advisory services"], cta: "Contact Sales" }
];

// Original Testimonials
const TESTIMONIALS = [
  { name: "Sarah Chen", role: "Founder, ContentCo", quote: "FinAI transformed how we handle finances. The AI-powered categorization saves us hours every week.", rating: 5 },
  { name: "Marcus Johnson", role: "CEO, PropertyVest LLC", quote: "The real-time insights have been incredible for managing cash flow across our properties.", rating: 5 },
  { name: "Emily Rodriguez", role: "Founder, Bloom Skincare", quote: "I keep more of what I earn thanks to FinAI's proactive tax strategies. Game changer for D2C brands.", rating: 5 }
];

// Original FAQs
const FAQS = [
  { q: "How is FinAI different from other bookkeeping services?", a: "FinAI combines AI-powered automation with expert human bookkeepers. Our technology handles the repetitive work while our team ensures accuracy and provides strategic insights." },
  { q: "What's included in the monthly bookkeeping service?", a: "Transaction categorization, bank reconciliation, financial statements (P&L, Balance Sheet, Cash Flow), and access to your dedicated bookkeeper for questions." },
  { q: "Can I switch plans or cancel anytime?", a: "Yes! You can upgrade, downgrade, or cancel your plan at any time. No long-term contracts required." },
  { q: "How does the tax planning work?", a: "Our tax experts review your financials quarterly to identify deductions, optimize your structure, and ensure you're never surprised at tax time." },
  { q: "Is my financial data secure?", a: "Absolutely. We use bank-level 256-bit encryption, SOC 2 compliance, and never share your data with third parties." }
];

// Stats
const STATS = [
  { value: "500+", label: "Active Clients" },
  { value: "$50M+", label: "Transactions Processed" },
  { value: "99%", label: "Client Satisfaction" }
];

// Trusted logos
const TRUSTED_LOGOS = ["TechCrunch", "Forbes", "Bloomberg", "Inc.", "Entrepreneur"];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ name: "", email: "", company_name: "", entity_type: "llc", services: [], message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setIsLoggedIn(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsLoggedIn(res.ok);
      } catch {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    localStorage.removeItem("auth_token");
    setIsLoggedIn(false);
    toast.success("Logged out successfully");
  };

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setMobileMenuOpen(false); };

  const handleCheckout = async (planId) => {
    if (planId === "enterprise") {
      setQuoteDialogOpen(true);
      return;
    }
    if (planId === "starter") { navigate("/register"); return; }
    
    const token = localStorage.getItem("auth_token");
    let isAuthenticated = false;
    try {
      const authRes = await fetch(`${API_URL}/api/auth/me`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      isAuthenticated = authRes.ok;
    } catch { isAuthenticated = false; }
    
    if (!isAuthenticated) { toast.info("Please sign in to subscribe"); navigate("/login", { state: { returnTo: `/pricing?plan=${planId}` } }); return; }
    
    setCheckoutLoading(planId);
    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), "Origin": window.location.origin },
        credentials: "include",
        body: JSON.stringify({ plan_id: planId, billing_cycle: "monthly" })
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || "Failed to create checkout"); }
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) { toast.error(err.message); } finally { setCheckoutLoading(null); }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/quotes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(quoteForm) });
      if (!res.ok) throw new Error("Failed");
      toast.success("We'll be in touch within 24 hours!");
      setQuoteDialogOpen(false);
      setQuoteForm({ name: "", email: "", company_name: "", entity_type: "llc", services: [], message: "" });
    } catch { toast.error("Failed to submit. Please try again."); } finally { setSubmitting(false); }
  };

  const toggleService = (service) => {
    setQuoteForm(prev => ({
      ...prev,
      services: prev.services.includes(service) 
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-[#F7FFE5]/95 backdrop-blur-sm border-b border-[#BFFF00]/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#BFFF00] rounded-xl flex items-center justify-center">
                <span className="text-[#121212] font-extrabold text-lg">F</span>
              </div>
              <span className="text-xl font-bold text-[#121212] tracking-tight font-['Manrope']">FinAI</span>
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1 bg-white rounded-full px-2 py-2 shadow-sm border border-gray-100">
              <button onClick={() => scrollTo("services")} className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#121212] hover:bg-gray-50 rounded-full transition-colors">Services</button>
              <button onClick={() => scrollTo("pricing")} className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#121212] hover:bg-gray-50 rounded-full transition-colors">Pricing</button>
              <button onClick={() => scrollTo("tools")} className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#121212] hover:bg-gray-50 rounded-full transition-colors">Free Tools</button>
              <button onClick={() => scrollTo("faq")} className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#121212] hover:bg-gray-50 rounded-full transition-colors">FAQ</button>
              {isLoggedIn ? (
                <button 
                  onClick={handleLogout} 
                  className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#121212] hover:bg-gray-50 rounded-full transition-colors flex items-center gap-1"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              ) : (
                <Link to="/login" className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#121212] hover:bg-gray-50 rounded-full transition-colors">Login</Link>
              )}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              {isLoggedIn ? (
                <Link to="/dashboard">
                  <Button className="h-11 px-6 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-medium" data-testid="nav-cta-btn">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/register">
                  <Button className="h-11 px-6 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-medium" data-testid="nav-cta-btn">
                    Get Started Free
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Header Actions */}
            <div className="lg:hidden flex items-center gap-2">
              {isLoggedIn ? (
                <Link to="/dashboard">
                  <Button size="sm" className="h-9 px-4 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-medium text-sm">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button size="sm" className="h-9 px-4 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-medium text-sm">
                    Login
                  </Button>
                </Link>
              )}
              <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t py-6 px-6">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollTo("services")} className="text-left py-2 font-medium">Services</button>
              <button onClick={() => scrollTo("pricing")} className="text-left py-2 font-medium">Pricing</button>
              <button onClick={() => scrollTo("tools")} className="text-left py-2 font-medium">Free Tools</button>
              <button onClick={() => scrollTo("faq")} className="text-left py-2 font-medium">FAQ</button>
              {isLoggedIn ? (
                <>
                  <button onClick={handleLogout} className="text-left py-2 font-medium flex items-center gap-2">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                  <Link to="/dashboard"><Button className="w-full h-11 rounded-full bg-[#121212]">Dashboard</Button></Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="py-2 font-medium">Login</Link>
                  <Link to="/register"><Button className="w-full h-11 rounded-full bg-[#121212]">Get Started Free</Button></Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden" style={{ background: 'linear-gradient(180deg, #F7FFE5 0%, #EDFFC2 50%, #ffffff 100%)' }}>
        {/* Animated gradient background */}
        <div className="absolute inset-0 opacity-30 animate-gradient-shift" style={{ 
          background: 'linear-gradient(45deg, #BFFF00, #F7FFE5, #a8e600, #EDFFC2, #BFFF00)',
          backgroundSize: '400% 400%'
        }} />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'linear-gradient(#BFFF00 1px, transparent 1px), linear-gradient(90deg, #BFFF00 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        
        {/* Animated floating blobs */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#BFFF00]/20 rounded-full blur-3xl animate-blob-morph" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#BFFF00]/15 rounded-full blur-3xl animate-blob-morph" style={{ animationDelay: '-4s' }} />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-[#a8e600]/10 rounded-full blur-2xl animate-blob-morph" style={{ animationDelay: '-2s' }} />
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float-particle"
              style={{
                width: `${4 + (i % 3) * 4}px`,
                height: `${4 + (i % 3) * 4}px`,
                background: i % 2 === 0 ? '#BFFF00' : '#121212',
                left: `${5 + i * 8}%`,
                top: `${10 + (i % 5) * 18}%`,
                animationDelay: `${i * 0.4}s`,
                animationDuration: `${5 + (i % 3) * 2}s`,
                opacity: 0.3 + (i % 3) * 0.1
              }}
            />
          ))}
        </div>
        
        {/* Orbiting elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none hidden lg:block">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 bg-[#BFFF00] rounded-full animate-orbit" style={{ animationDuration: '25s' }} />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-[#121212] rounded-full animate-orbit-reverse" style={{ animationDuration: '20s' }} />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 bg-[#BFFF00]/50 rounded-full animate-orbit" style={{ animationDuration: '30s', animationDelay: '-5s' }} />
          </div>
        </div>
        
        {/* Shimmer line effect */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#BFFF00] to-transparent overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-12 z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold text-[#121212] uppercase tracking-widest mb-6">TRUSTED BY 500+ BUSINESSES</p>
              <h1 className="font-extrabold text-[#121212] tracking-tight mb-6 font-['Manrope']">
                <span className="block text-3xl sm:text-4xl lg:text-5xl leading-tight mb-2">AI-Powered</span>
                <span className="block mb-3">
                  <span className="inline-block px-3 sm:px-4 py-1 bg-[#BFFF00] rounded-full text-2xl sm:text-3xl lg:text-4xl">Bookkeeping</span>
                  <span className="mx-1 sm:mx-2 text-2xl sm:text-3xl lg:text-4xl">&</span>
                </span>
                <span className="block mb-3">
                  <span className="inline-block px-3 sm:px-4 py-1 border-2 border-[#121212] rounded-full text-2xl sm:text-3xl lg:text-4xl">Accounting</span>
                </span>
                <span className="block text-2xl sm:text-3xl lg:text-4xl leading-tight">for Creators & Founders</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-[#666] leading-relaxed mb-8 max-w-lg">
                Clean books, sharp insights, zero chaos. Professional bookkeeping backed by AI and expert accountants.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-[#BFFF00] hover:bg-[#a8e600] text-[#121212] text-base sm:text-lg font-semibold shadow-lg shadow-[#BFFF00]/30 transition-all hover:scale-105" data-testid="hero-cta-btn">
                    Start Free Trial <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </Button>
                </Link>
                <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 rounded-full border-2 border-[#121212] text-[#121212] text-base sm:text-lg font-semibold hover:bg-[#121212] hover:text-white transition-all">
                      Get Custom Quote
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg rounded-3xl">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold font-['Manrope']">Get a Custom Quote</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleQuoteSubmit} className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Name *</Label><Input value={quoteForm.name} onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})} required className="rounded-xl" /></div>
                        <div><Label>Email *</Label><Input type="email" value={quoteForm.email} onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})} required className="rounded-xl" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Company Name</Label><Input value={quoteForm.company_name} onChange={(e) => setQuoteForm({...quoteForm, company_name: e.target.value})} className="rounded-xl" /></div>
                        <div>
                          <Label>Entity Type</Label>
                          <select value={quoteForm.entity_type} onChange={(e) => setQuoteForm({...quoteForm, entity_type: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200">
                            <option value="individual">Individual</option>
                            <option value="llc">LLC</option>
                            <option value="s-corp">S-Corp</option>
                            <option value="c-corp">C-Corp</option>
                            <option value="partnership">Partnership</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label>Services Needed</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {["Bookkeeping", "Tax Planning", "Payroll", "FP&A", "CFO Services", "Other"].map((service) => (
                            <label key={service} className="flex items-center gap-2 cursor-pointer">
                              <Checkbox checked={quoteForm.services.includes(service)} onCheckedChange={() => toggleService(service)} />
                              <span className="text-sm">{service}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div><Label>Additional Details</Label><Textarea value={quoteForm.message} onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})} className="rounded-xl" placeholder="Tell us about your business..." /></div>
                      <Button type="submit" className="w-full h-12 rounded-full bg-[#121212]" disabled={submitting}>
                        {submitting ? "Sending..." : "Request Quote"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            {/* Floating Cards */}
            <div className="relative h-[500px] hidden lg:block">
              {/* Revenue Card */}
              <div className="absolute top-0 right-10 bg-white rounded-3xl p-6 shadow-2xl w-64 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-[#666]">Monthly Revenue</span>
                  <span className="text-xs bg-[#BFFF00] text-[#121212] px-2 py-1 rounded-full font-bold">Live</span>
                </div>
                <p className="text-3xl font-bold text-[#121212] mb-2">$124,500</p>
                <p className="text-sm text-[#22c55e] flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" /> +12.5% from last month
                </p>
              </div>

              {/* Expenses Pie Chart */}
              <div className="absolute top-48 left-0 bg-white rounded-3xl p-6 shadow-2xl w-56 border border-gray-100">
                <p className="text-sm font-medium text-[#121212] mb-4">Expense Breakdown</p>
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20">
                    <svg className="w-20 h-20 -rotate-90">
                      <circle cx="40" cy="40" r="32" fill="none" stroke="#E5E7EB" strokeWidth="8" />
                      <circle cx="40" cy="40" r="32" fill="none" stroke="#BFFF00" strokeWidth="8" strokeDasharray="200" strokeDashoffset="50" />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">75%</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center gap-2"><span className="w-2 h-2 bg-[#BFFF00] rounded-full"></span> Operations</div>
                    <div className="flex items-center gap-2"><span className="w-2 h-2 bg-[#121212] rounded-full"></span> Marketing</div>
                    <div className="flex items-center gap-2"><span className="w-2 h-2 bg-gray-300 rounded-full"></span> Other</div>
                  </div>
                </div>
              </div>

              {/* Rating Card */}
              <div className="absolute bottom-20 right-0 bg-white rounded-full px-6 py-4 shadow-2xl flex items-center gap-4 border border-gray-100">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#121212]">4.9</p>
                  <div className="flex gap-0.5 mt-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#BFFF00] text-[#BFFF00]" />)}
                  </div>
                  <p className="text-xs text-[#666] mt-1">500+ reviews</p>
                </div>
              </div>

              {/* Tax Savings Card */}
              <div className="absolute bottom-0 left-20 bg-[#121212] text-white rounded-2xl p-4 shadow-2xl">
                <p className="text-xs text-gray-400 mb-1">Tax Savings</p>
                <p className="text-2xl font-bold">$32,450</p>
                <p className="text-xs text-[#BFFF00]">This Year</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#BFFF00] uppercase tracking-widest mb-4">OUR SERVICES</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#121212] tracking-tight mb-4 font-['Manrope']">Everything You Need to Scale</h2>
            <p className="text-[#666] text-lg max-w-2xl mx-auto">From bookkeeping to tax strategy, we handle your finances so you can focus on growth.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((service, idx) => (
              <div key={idx} className="group bg-white border-2 border-gray-100 rounded-3xl p-8 hover:border-[#BFFF00] hover:shadow-xl hover:shadow-[#BFFF00]/10 transition-all duration-300">
                <div className="w-14 h-14 bg-[#F7FFE5] rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#BFFF00] transition-colors">
                  <service.icon className="w-7 h-7 text-[#121212]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-[#121212] mb-3 font-['Manrope']">{service.title}</h3>
                <p className="text-[#666] text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-6 lg:px-12" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #F7FFE5 50%, #ffffff 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="border-2 border-dashed border-[#BFFF00] rounded-3xl py-12 px-8 bg-white/50 backdrop-blur">
            <div className="grid grid-cols-3 gap-8 text-center">
              {STATS.map((stat, idx) => (
                <div key={idx}>
                  <div className="text-4xl md:text-5xl font-extrabold text-[#121212] tracking-tight font-['Manrope']">{stat.value}</div>
                  <div className="text-[#666] mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing - MOVED ABOVE FREE TOOLS */}
      <section id="pricing" className="relative py-24 px-6 lg:px-12 overflow-hidden bg-white">
        {/* Lime Gradient Top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-[#BFFF00]/30 to-transparent rounded-b-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#BFFF00] uppercase tracking-widest mb-4">PRICING</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#121212] tracking-tight mb-4 font-['Manrope']">Simple, Transparent Pricing</h2>
            <p className="text-[#666] text-lg">Start free, upgrade as you grow. No hidden fees.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRICING_PLANS.map((plan) => (
              <div key={plan.id} className={`relative bg-white rounded-3xl p-8 border-2 transition-all hover:shadow-xl ${plan.popular ? 'border-[#BFFF00] shadow-lg shadow-[#BFFF00]/20 scale-105' : 'border-dashed border-gray-200 hover:border-[#BFFF00]'}`}>
                {plan.popular && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#BFFF00] text-[#121212] text-sm font-bold px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="text-xl font-bold text-[#121212] mb-1 font-['Manrope']">{plan.name}</h3>
                <p className="text-sm text-[#666] mb-4">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-extrabold text-[#121212] font-['Manrope']">{plan.price}</span>
                  <span className="text-[#666]">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#666]">
                      <div className="w-5 h-5 bg-[#BFFF00] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#121212]" />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleCheckout(plan.id)}
                  disabled={checkoutLoading === plan.id}
                  className={`w-full h-12 rounded-full font-semibold transition-all ${plan.popular ? 'bg-[#121212] text-white hover:bg-[#2a2a2a]' : 'bg-gray-100 text-[#121212] hover:bg-[#BFFF00]'}`}
                  data-testid={`pricing-${plan.id}-btn`}
                >
                  {checkoutLoading === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Tools */}
      <section id="tools" className="py-24 px-6 lg:px-12" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #F7FFE5 50%, #ffffff 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#BFFF00] uppercase tracking-widest mb-4">FREE TOOLS</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#121212] tracking-tight mb-4 font-['Manrope']">Try Our Free Financial Tools</h2>
            <p className="text-[#666] text-lg">Powerful calculators to help you make smarter financial decisions</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {FREE_TOOLS.map((tool, idx) => (
              <Link key={idx} to={tool.path} className="group bg-white rounded-3xl p-8 hover:shadow-xl hover:shadow-[#BFFF00]/20 transition-all duration-300 hover:-translate-y-2 border-2 border-dashed border-[#BFFF00]/30 hover:border-[#BFFF00]">
                <div className="w-14 h-14 bg-[#F7FFE5] rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#BFFF00] transition-colors">
                  <tool.icon className="w-7 h-7 text-[#121212]" />
                </div>
                <h3 className="text-xl font-bold text-[#121212] mb-2 font-['Manrope']">{tool.name}</h3>
                <p className="text-[#666] text-sm mb-4">{tool.description}</p>
                <span className="text-[#121212] font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                  Try Now Free <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 px-6 lg:px-12 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#BFFF00] uppercase tracking-widest mb-4">TESTIMONIALS</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#121212] tracking-tight font-['Manrope']">Loved by Founders</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, idx) => (
              <div key={idx} className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-shadow border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#BFFF00] text-[#BFFF00]" />)}
                </div>
                <p className="text-[#121212] leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#BFFF00] rounded-full flex items-center justify-center text-[#121212] font-bold">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-bold text-[#121212] font-['Manrope']">{t.name}</p>
                    <p className="text-sm text-[#666]">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#BFFF00] uppercase tracking-widest mb-4">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#121212] tracking-tight font-['Manrope']">Common Questions</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="bg-white border-2 border-gray-100 rounded-2xl px-6 data-[state=open]:border-[#BFFF00] transition-colors">
                <AccordionTrigger className="text-left font-bold text-[#121212] hover:no-underline py-6 font-['Manrope']">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#666] pb-6">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-12 bg-[#121212]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6 font-['Manrope']">Ready to Simplify Your Finances?</h2>
          <p className="text-xl text-gray-400 mb-10">Join 500+ businesses using FinAI to save time and money.</p>
          <Link to="/register">
            <Button className="h-14 px-10 rounded-full bg-[#BFFF00] text-[#121212] text-lg font-bold hover:bg-[#a8e600] transition-all hover:scale-105 shadow-lg shadow-[#BFFF00]/30" data-testid="footer-cta-btn">
              Start Your Free Trial <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <div className="flex flex-wrap justify-center gap-8 mt-10 text-sm text-gray-400">
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#BFFF00]" /> No Credit Card Required</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#BFFF00]" /> 14-Day Free Trial</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#BFFF00]" /> Cancel Anytime</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 lg:px-12 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#BFFF00] rounded-lg flex items-center justify-center">
                  <span className="text-[#121212] font-bold">F</span>
                </div>
                <span className="text-xl font-bold text-[#121212] font-['Manrope']">FinAI</span>
              </div>
              <p className="text-sm text-[#666] mb-4">AI-powered bookkeeping and accounting for creators, founders, and growing businesses.</p>
              <p className="text-xs text-[#999]">by revurge.</p>
            </div>
            <div>
              <h4 className="font-bold text-[#121212] mb-4 font-['Manrope']">Services</h4>
              <ul className="space-y-2 text-sm text-[#666]">
                <li><button onClick={() => scrollTo("services")} className="hover:text-[#121212]">Bookkeeping</button></li>
                <li><button onClick={() => scrollTo("services")} className="hover:text-[#121212]">Tax Planning</button></li>
                <li><button onClick={() => scrollTo("services")} className="hover:text-[#121212]">FP&A</button></li>
                <li><button onClick={() => scrollTo("services")} className="hover:text-[#121212]">CFO Advisory</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#121212] mb-4 font-['Manrope']">Company</h4>
              <ul className="space-y-2 text-sm text-[#666]">
                <li><Link to="/pricing" className="hover:text-[#121212]">Pricing</Link></li>
                <li><Link to="/tools/tax-calculator" className="hover:text-[#121212]">Free Tools</Link></li>
                <li><button onClick={() => setQuoteDialogOpen(true)} className="hover:text-[#121212]">Contact</button></li>
                <li>
                  {isLoggedIn ? (
                    <Link to="/dashboard" className="hover:text-[#121212]">Dashboard</Link>
                  ) : (
                    <Link to="/login" className="hover:text-[#121212]">Login</Link>
                  )}
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#121212] mb-4 font-['Manrope']">Contact</h4>
              <ul className="space-y-3 text-sm text-[#666]">
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> info@revurge.com</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> 8 The Green, Dover, DE 19901</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[#666]">© 2026 FinAI by Revurge. All rights reserved.</p>
            <div className="flex flex-wrap gap-4 md:gap-6 text-sm text-[#666]">
              <Link to="/privacy" className="hover:text-[#121212]">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#121212]">Terms of Service</Link>
              <Link to="/security" className="hover:text-[#121212]">Security</Link>
              <Link to="/faq" className="hover:text-[#121212]">FAQ</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
